import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/I18nContext";
import { Crown, Medal, Plus } from "lucide-react";
import { toast } from "sonner";

export default function Ranking() {
  const { t } = useI18n();
  const [pools, setPools] = useState([]);
  const [selectedPool, setSelectedPool] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joinCode, setJoinCode] = useState("");
  const [showJoin, setShowJoin] = useState(false);

  const loadPools = async () => {
    const r = await api.get("/pools");
    setPools(r.data);
    if (r.data.length > 0 && !selectedPool) {
      setSelectedPool(r.data[0].pool_id);
    }
  };

  useEffect(() => { loadPools(); }, []);

  useEffect(() => {
    if (!selectedPool) { setLoading(false); return; }
    let cancelled = false;
    const fetchRanking = async (showLoading = false) => {
      if (showLoading) setLoading(true);
      try {
        const r = await api.get(`/pools/${selectedPool}/ranking`);
        if (!cancelled) setRows(r.data);
      } catch {
        if (!cancelled && showLoading) setRows([]);
      } finally {
        if (!cancelled && showLoading) setLoading(false);
      }
    };
    fetchRanking(true);
    const onVis = () => { if (document.visibilityState === "visible") fetchRanking(false); };
    document.addEventListener("visibilitychange", onVis);
    const interval = setInterval(() => fetchRanking(false), 45000);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVis);
      clearInterval(interval);
    };
  }, [selectedPool]);

  const joinPool = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/pools/join", { invite_code: joinCode.trim().toUpperCase() });
      toast.success(`Unido a "${r.data.name}"`);
      setJoinCode("");
      setShowJoin(false);
      await loadPools();
      setSelectedPool(r.data.pool_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const currentPool = pools.find((p) => p.pool_id === selectedPool);

  return (
    <div className="space-y-6" data-testid="ranking-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono-data mb-2">LEADERBOARD</div>
          <h1 className="font-display text-5xl md:text-6xl uppercase leading-none">{t("ranking.title")}</h1>
          <p className="text-zinc-400 text-sm mt-2">{t("ranking.subtitle")}</p>
        </div>
      </div>

      {/* Pool selector tabs */}
      <div className="overflow-x-auto" data-testid="pool-selector">
        <div className="flex gap-2 min-w-max">
          {pools.map((p) => (
            <button key={p.pool_id} onClick={() => setSelectedPool(p.pool_id)}
              data-testid={`pool-tab-${p.pool_id}`}
              className={`px-4 py-3 text-xs uppercase tracking-widest whitespace-nowrap transition-colors border ${
                selectedPool === p.pool_id
                  ? "bg-[var(--c-lime)] text-black border-[var(--c-lime)] font-bold"
                  : "border-zinc-800 text-zinc-400 hover:border-white"
              }`}>
              {p.name} <span className="ml-1 font-mono-data opacity-70">({p.member_count})</span>
              {p.is_default && <span className="ml-1 text-[9px]">★</span>}
            </button>
          ))}
        </div>
      </div>

      {currentPool?.prize && (
        <PrizeBox prize={currentPool.prize} t={t} />
      )}

      {loading ? (
        <div className="text-zinc-400">{t("ranking.loading")}</div>
      ) : (
        <>
          {rows.length >= 3 && (
            <div className="grid grid-cols-3 gap-2 md:gap-4 items-end">
              <PodiumCard rank={2} row={rows[1]} bg="#E5E7EB" iconColor="black" textColor="black" h="h-32 md:h-40" pct={currentPool?.prize?.percent_second} />
              <PodiumCard rank={1} row={rows[0]} bg="var(--c-yellow)" iconColor="black" textColor="black" h="h-40 md:h-56" big pct={currentPool?.prize?.percent_first} />
              <PodiumCard rank={3} row={rows[2]} bg="var(--c-peach)" iconColor="black" textColor="black" h="h-28 md:h-36" pct={currentPool?.prize?.percent_third} />
            </div>
          )}

          <div className="bg-zinc-900 border border-zinc-800" data-testid="ranking-table">
            <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data">
              <div className="col-span-1">{t("ranking.colPos")}</div>
              <div className="col-span-9">{t("ranking.colPlayer")}</div>
              <div className="col-span-2 text-right">{t("ranking.colPoints")}</div>
            </div>
            {rows.length === 0 && (
              <div className="p-6 text-center text-zinc-500 text-sm">{t("ranking.empty")}</div>
            )}
            {rows.map((r) => (
              <div key={r.user_id} data-testid={`ranking-row-${r.user_id}`} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-zinc-800 items-center hover:bg-zinc-800/40 transition-colors">
                <div className="col-span-1 font-mono-data font-bold">
                  <span className={r.position === 1 ? "text-[var(--c-yellow)]" : r.position === 2 ? "text-zinc-200" : r.position === 3 ? "text-[var(--c-peach)]" : ""}>
                    {r.position}
                  </span>
                </div>
                <div className="col-span-9 flex items-center gap-2">
                  <div className="font-semibold" translate="no">{r.nickname}</div>
                  {r.position === 1 && <Crown size={14} className="text-[var(--c-yellow)]" />}
                  {(r.position === 2 || r.position === 3) && <Medal size={12} className="text-zinc-400" />}
                </div>
                <div className="col-span-2 text-right font-mono-data text-lg font-bold">
                  {r.points}<span className="text-[10px] text-zinc-500 ml-1">PTS</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function PrizeBox({ prize, t }) {
  return (
    <div
      className="border border-zinc-800 px-6 py-5 md:py-6 text-center"
      data-testid="prize-box"
    >
      <div className="font-display text-xl md:text-2xl uppercase text-white tracking-wide">
        {t("ranking.prize.title")}
      </div>
      <div className="font-display text-3xl md:text-5xl text-white leading-none mt-2 break-words" data-testid="prize-amount">
        {prize.amount_text}
      </div>
    </div>
  );
}

function PodiumCard({ rank, row, bg, iconColor, textColor, h, big, pct }) {
  return (
    <div className={`flex flex-col justify-end items-center ${h}`} data-testid={`podium-${rank}`}>
      <div className="text-center mb-2">
        <div className="font-display text-lg md:text-2xl" translate="no">{row.nickname}</div>
        <div className="font-mono-data text-xs text-zinc-400">{row.points} PTS</div>
      </div>
      <div className="w-full flex-1 flex flex-col items-center justify-center" style={{ background: bg }}>
        {pct != null && (
          <div
            className="font-display text-base md:text-2xl leading-none"
            style={{ color: textColor || iconColor }}
            data-testid={`podium-pct-${rank}`}
          >
            {pct}%
          </div>
        )}
        <div className="font-display text-5xl md:text-7xl" style={{ color: textColor || iconColor }}>{rank}</div>
        {big && <Crown size={28} style={{ color: iconColor }} className="mt-1" />}
      </div>
    </div>
  );
}
